import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../contexts/AdminContext';
import { useMember } from '../contexts/MemberContext';
import { signIn, checkSubscriptionStatus } from '../utils/auth';
import { supabase } from '../utils/supabaseClient';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminLogin } = useAdmin();
  const { isMember, startMembership } = useMember();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Load saved credentials if remember me was checked
    try {
      const savedEmail = localStorage.getItem('rememberedEmail');
      const savedPassword = localStorage.getItem('rememberedPassword');
      const wasRemembered = localStorage.getItem('rememberMe') === 'true';
      
      if (wasRemembered && savedEmail) {
        setFormData({
          email: savedEmail,
          password: savedPassword || ''
        });
        setRememberMe(true);
      }
    } catch (e) {
      console.error('Failed to load saved credentials:', e);
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Save or clear credentials based on remember me checkbox
    try {
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
        localStorage.setItem('rememberedPassword', formData.password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberMe');
      }
    } catch (e) {
      console.error('Failed to save credentials:', e);
    }
    
    // Check demo credentials
    if (formData.email === 'demo@alera.com' && formData.password === 'alera2025') {
      try {
        // Sign in with Supabase Auth for demo user
        const result = await signIn(formData.email, formData.password);
        
        if (result.success) {
          // Store user email for data sync
          localStorage.setItem('userEmail', formData.email);
          // Grant temporary membership for demo session
          startMembership({ planId: 'freedom', source: 'demo' });
          navigate('/dashboard');
        } else {
          setError('Demo login failed. Please contact support.');
        }
      } catch (err) {
        setError('Demo login error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Try Supabase Auth login for admin/member accounts
      try {
        console.log('🔐 Attempting Supabase Auth login for:', formData.email);
        const result = await signIn(formData.email, formData.password);
        
        console.log('🔐 Login result:', result);
        
        if (result.success) {
          console.log('✅ Supabase Auth login successful!');
          // Store user email for data sync
          localStorage.setItem('userEmail', formData.email);
          
          // Check subscription status in Supabase before granting access
          const subStatus = await checkSubscriptionStatus(formData.email);
          console.log('📋 Subscription status:', subStatus);
          
          if (subStatus === 'active') {
            startMembership({ planId: 'freedom', source: 'auth' });
            navigate('/dashboard');
          } else if (subStatus === 'past_due') {
            setError('Your payment failed. Please contact hello@alera.co.nz to update your payment method and restore access.');
          } else if (subStatus === 'cancelled') {
            setError('Your subscription has been cancelled. Please resubscribe to access the community.');
          } else {
            // No member record found — could be admin or unsubscribed
            const user = result.user;
            if (user?.user_metadata?.role === 'admin' || user?.app_metadata?.role === 'admin') {
              startMembership({ planId: 'freedom', source: 'admin' });
              navigate('/dashboard');
            } else {
              setError('No active subscription found. Please subscribe to access the community.');
            }
          }
        } else {
          console.log('❌ Supabase Auth failed:', result.error);
          setError('Invalid email or password.');
        }
      } catch (err) {
        console.error('❌ Login error:', err);
        setError('Login failed. Please check your credentials.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-beige-50 to-rose-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="mt-6 text-3xl font-playfair font-semibold text-charcoal-900">
            Welcome Back
          </h2>
          <p className="mt-2 text-sm text-charcoal-600">
            Sign in to access your exclusive community content
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-8 space-y-6 bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-soft"
          onSubmit={handleSubmit}
        >
          {/* Banner if redirected due to membership requirement */}
          {location.state?.reason === 'membership_required' && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              Please sign in with a Freedom membership to access this page. If you don't have one yet, join below.
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-charcoal-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                style={{ borderColor: '#f0e0dc' }}
                className="appearance-none relative block w-full px-4 py-3 border placeholder-charcoal-400 text-charcoal-900 rounded-2xl focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-all duration-200"
                onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-charcoal-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                style={{ borderColor: '#f0e0dc' }}
                className="appearance-none relative block w-full px-4 py-3 border placeholder-charcoal-400 text-charcoal-900 rounded-2xl focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-all duration-200"
                onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: '#d9a79c' }}
                className="h-4 w-4 border rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-charcoal-600">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{ color: '#d9a79c' }}
                className="font-medium hover:opacity-80 transition-all duration-200"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              style={{ backgroundColor: '#d9a79c' }}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-2xl text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-soft hover:shadow-soft-lg"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in to Community'
              )}
            </motion.button>
          </div>

          <div className="text-center">
            <p className="text-sm text-charcoal-600">
              Don't have an account?{' '}
              <RouterLink to="/signup" style={{ color: '#d9a79c' }} className="font-medium hover:opacity-80 transition-all duration-200">
                Join the Alera Community
              </RouterLink>
            </p>
          </div>
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <RouterLink
            to="/"
            className="text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors duration-200"
          >
            ← Back to Home
          </RouterLink>
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
          >
            <h2 className="text-2xl font-semibold text-charcoal-900 mb-2">Reset Your Password</h2>
            <p className="text-sm text-charcoal-600 mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {resetSuccess ? (
              <div className="text-center py-4">
                <div className="text-5xl mb-4">✅</div>
                <p className="text-green-700 font-medium mb-2">Email Sent!</p>
                <p className="text-sm text-charcoal-600 mb-6">
                  Check your inbox for a password reset link. Don't forget to check your spam folder.
                </p>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetSuccess(false);
                    setResetEmail('');
                  }}
                  style={{ backgroundColor: '#d9a79c' }}
                  className="w-full py-3 px-4 text-white rounded-2xl font-medium hover:opacity-90 transition-all"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setResetLoading(true);
                setResetError('');

                try {
                  const response = await fetch('/.netlify/functions/send-password-reset', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: resetEmail })
                  });

                  const result = await response.json();

                  if (!response.ok) {
                    throw new Error(result.error || 'Failed to send reset email');
                  }

                  setResetSuccess(true);
                } catch (err) {
                  console.error('Password reset request failed:', err);
                  setResetError(
                    err?.message || 'Failed to send reset email. Please try again in a moment.'
                  );
                } finally {
                  setResetLoading(false);
                }
              }}>
                {resetError && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm mb-4">
                    {resetError}
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    style={{ borderColor: '#f0e0dc' }}
                    className="w-full px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetError('');
                    }}
                    className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    style={{ backgroundColor: '#d9a79c' }}
                    className="flex-1 py-3 px-4 text-white rounded-2xl font-medium hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Login;
